import React, { useMemo } from 'react';
import {
  ComposedChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceDot,
} from 'recharts';

function fmt(s) {
  const total = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `0:${sec.toString().padStart(2, '0')}`;
}

const COLOR_A = '#18181B';
const COLOR_B = '#D97706';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const a = payload.find(p => p.dataKey === 'a')?.value;
  const b = payload.find(p => p.dataKey === 'b')?.value;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E4E4E7',
      borderRadius: 8,
      padding: '10px 14px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    }}>
      <p style={{ color: '#9CA3AF', fontFamily: 'var(--font-mono)', fontSize: 10, marginBottom: 6 }}>
        t = {fmt(label)}
      </p>
      {a != null && (
        <p style={{ color: COLOR_A, fontSize: 12, margin: '0 0 3px', fontWeight: 600 }}>
          A — {a.toFixed(1)} act
        </p>
      )}
      {b != null && (
        <p style={{ color: COLOR_B, fontSize: 12, margin: 0, fontWeight: 600 }}>
          B — {b.toFixed(1)} act
        </p>
      )}
    </div>
  );
};

export default function DualEngagementCurve({ timeline, sideA, sideB, currentTime, onTimeChange }) {
  const data = useMemo(() => timeline || [], [timeline]);

  const domainMin = useMemo(() => {
    const vals = data.flatMap(d => [d.a, d.b]).filter(v => v != null);
    return Math.max(0, Math.min(...vals) - 5);
  }, [data]);

  const domainMax = useMemo(() => {
    const vals = data.flatMap(d => [d.a, d.b]).filter(v => v != null);
    return Math.min(100, Math.max(...vals) + 5);
  }, [data]);

  const handleClick = e => {
    if (e?.activeLabel !== undefined && onTimeChange) onTimeChange(e.activeLabel);
  };

  if (!data.length) return null;

  return (
    <div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 14, alignItems: 'center' }}>
        <LegendLine color={COLOR_A} label={sideA?.name || 'A'} />
        <LegendLine color={COLOR_B} label={sideB?.name || 'B'} />
        {sideA?.baseline_activation && (
          <LegendLine color="#D1D5DB" label="Baselines" dashed />
        )}
      </div>

      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} onClick={handleClick} style={{ cursor: 'pointer' }}>
            <defs>
              <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={COLOR_A} stopOpacity={0.08} />
                <stop offset="95%" stopColor={COLOR_A} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradB" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={COLOR_B} stopOpacity={0.08} />
                <stop offset="95%" stopColor={COLOR_B} stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="t"
              tick={{ fill: '#9CA3AF', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              tickLine={false} axisLine={false}
              tickFormatter={fmt}
              interval={Math.max(1, Math.floor(data.length / 8))}
            />
            <YAxis domain={[domainMin, domainMax]} hide />
            <Tooltip content={<CustomTooltip />} />

            {/* Baselines */}
            {sideA?.baseline_activation && (
              <ReferenceLine y={sideA.baseline_activation}
                stroke="#D1D5DB" strokeDasharray="4 4" strokeWidth={1} />
            )}
            {sideB?.baseline_activation && (
              <ReferenceLine y={sideB.baseline_activation}
                stroke="#FDE68A" strokeDasharray="4 4" strokeWidth={1} />
            )}

            {/* Peak markers */}
            {sideA?.peak_second != null && sideA?.attention?.[sideA.peak_second] != null && (
              <ReferenceDot
                x={sideA.peak_second} y={sideA.attention[sideA.peak_second]}
                r={4} fill={COLOR_A} stroke="#fff" strokeWidth={1.5} />
            )}
            {sideB?.peak_second != null && sideB?.attention?.[sideB.peak_second] != null && (
              <ReferenceDot
                x={sideB.peak_second} y={sideB.attention[sideB.peak_second]}
                r={4} fill={COLOR_B} stroke="#fff" strokeWidth={1.5} />
            )}

            {/* Current time cursor */}
            {currentTime !== undefined && (
              <ReferenceLine x={Math.round(currentTime)}
                stroke="#9CA3AF" strokeWidth={1} strokeOpacity={0.7} />
            )}

            <Area
              type="monotone" dataKey="b"
              stroke={COLOR_B} strokeWidth={1.5}
              fill="url(#gradB)"
              dot={false}
              activeDot={{ r: 3, fill: COLOR_B, stroke: '#fff', strokeWidth: 1 }}
              connectNulls
            />
            <Area
              type="monotone" dataKey="a"
              stroke={COLOR_A} strokeWidth={2}
              fill="url(#gradA)"
              dot={false}
              activeDot={{ r: 4, fill: COLOR_A, stroke: '#fff', strokeWidth: 1.5 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
        <StatGroup label="A" color={COLOR_A} side={sideA} />
        <div style={{ width: 1, background: '#E4E4E7', alignSelf: 'stretch' }} />
        <StatGroup label="B" color={COLOR_B} side={sideB} />
      </div>
    </div>
  );
}

function StatGroup({ label, color, side }) {
  if (!side?.tribe_v2_ready) return null;
  return (
    <div style={{ display: 'flex', gap: 24 }}>
      <Stat label={`${label} AVG`} value={side.avg_activation?.toFixed(1)} color={color} />
      <Stat label={`${label} PEAK`} value={fmt(side.peak_second ?? 0)} color={color} />
      <Stat
        label={`${label} DROPS`}
        value={side.drops?.length ?? 0}
        color={side.drops?.length > 0 ? '#DC2626' : color}
      />
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#9CA3AF', letterSpacing: '0.1em', margin: '0 0 3px' }}>
        {label}
      </p>
      <p style={{ fontSize: 18, fontWeight: 700, color: color || '#09090B', margin: 0, lineHeight: 1 }}>
        {value ?? '—'}
      </p>
    </div>
  );
}

function LegendLine({ color, label, dashed }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 18, height: 2, flexShrink: 0,
        background: dashed ? 'none' : color,
        borderTop: dashed ? `2px dashed ${color}` : 'none',
      }} />
      <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'var(--font-mono)' }}>
        {label}
      </span>
    </div>
  );
}
